import React, { useState, useEffect } from 'react';
import { PrimaryButton, SecondaryButton } from "../UI";
import ConfigureExperience from '../Experience/ConfigureExperience';
import ChooseTemplate from '../Template/ChooseTemplate';
import tickImg from '../../assets/tick-step.svg';
import ErrorAlert from '../UI/Alert/ErrorAlert';
import { activateTealiumExperience, createExperience, createTenantActivityOffers, delinkOfferFromExperience, editExperience, editExperienceOfferMapping, getOfferExperienceMappings, getTealiumExperiences, updateTealiumExperience } from '../../utils/APIs';
import { useUserContext } from '../../contexts/UserContext';
import Loader from '../UI/Loader/Loader';
import { useNavigate, useParams } from 'react-router-dom';
import { compareJson, getNestedData, getObjFromHtml, isValidJsonString, updateHtmlTemplate } from '../../utils/helper';
import SuccessAlert from '../UI/Alert/SuccessAlert';
import Input from '../UI/Input';
import { permission } from '../../config/permission';
import OfferCode from '../Offers/offerCode';
import DropdownButton from './DropdownButton';
import CreateOfferAndExperienceModal from '../ManageAEMContentFragments/CreateOfferAndExperienceModal';
import { toast } from 'react-toastify';
import Activation from '../ExperienceCode/Activation';
import EditJsonPathModal from '../ManageAEMContentFragments/EditJsonPathModal';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import UpdateTealiumAssociationModal from './UpdateTealiumAssociationModal';

const TealiumExperienceDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enums, content: contentfulData } = useContentfulContext();
    const { enablePreviewMode, disablePreviewMode } = usePreviewMode();

    const [currentStep, setCurrentStep] = useState('choose-template');
    const [isStepperHidden, setIsStepperHidden] = useState(false);
    const [tealiumExperience, setTealiumExperience] = useState()
    const [tealiumExperienceName, setTealiumExperienceName] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const { userData, checkPermission } = useUserContext();
    const [selectedTemplate, setSelectedTemplate] = useState({});
    const [fieldValues, setFieldValues] = useState();
    const [transformedExperienceJSON, setTransformedExperienceJSON] = useState();
    const [fullEntryData, setFullEntryData] = useState(null);
    const [originalTealiumExperience, setOriginalTealiumExperience] = useState();
    const [validationError, setValidationError] = useState("");
    const [showPreview, setShowPreview] = useState(false);
    const [showCreateOfferModal, setShowCreateOfferModal] = useState();
    const [type, setType] = useState('');
    const [jsonPathModal, setJsonPathModal] = useState({ isOpen: false, type: null, tealiumExperience: null });
    const [experience, setExperience] = useState(null);
    const [fieldKey, setFieldKey] = useState(null);

    const [showUpdateOfferModal, setShowUpdateOfferModal] = useState(false);
    const [associatedExperiences, setAssociatedExperiences] = useState([]);

    useEffect(() => {
        getTealiumExperience()
        getEntryExpMappings()
    }, [])

    async function getEntryExpMappings() {
        try {
            const mappings = await getOfferExperienceMappings(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                id,
                userData?.defaultTealiumKey?._id
            );
            setAssociatedExperiences(mappings?.data || []);
        } catch (error) {
            console.log(error)
        }
    }

    const getTealiumExperience = async () => {
        try {
            if (!userData?.defaultTealiumKey) {
                toast.error(contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.errors.no_tealium_entry_found);
                return;
            }
            setLoading(true);
            const result = await getTealiumExperiences({
                token: userData?.token,
                organizationId: userData?.selectedOrganization?.org_token,
                tealium_id: userData?.defaultTealiumKey?._id,
                id: id,
            });
            
            setTealiumExperience(result?.data?.extensions);
            setTealiumExperienceName(result?.data?.extensions?.name);
            setOriginalTealiumExperience(result?.data?.extensions);

        } catch (error) {
            toast.error(error?.message || contentfulData?.COMMON_MESSAGE.something_wrong_msg)
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const timer = setTimeout(() => {
            setError('');
            setSuccessMessage('');
        }, 4000);

        return () => clearTimeout(timer);
    }, [error, successMessage]);

    const jsonPathModalClose = () => {
        // If the state hasn't been updated or user hasn't made changes
        if (!tealiumExperience || !originalTealiumExperience) {
            navigate("/experience/tealium-experiences");
        } else {
            setJsonPathModal({
                isOpen: false,
                type: "",
                tealiumExperience: null
            });
        }
    };

    function isFieldsJson(fields) {
        // If there is only **one key**, and that key's value is an object (not array/null)
        const keys = Object.keys(fields);

        if (keys.length === 1) {
            const value = fields[keys[0]];
            return (
                typeof value === 'object' &&
                value !== null &&
                !Array.isArray(value) &&
                Object.keys(value).length > 0
            );
        }

        return false; // More than 1 field => not JSON
    }

    const updateTealiumContentFromPath = (jsonPath, fullData) => {
        let pathData = getNestedData(fullData, jsonPath);

        const keys = Object.keys(pathData)
        if (keys.length === 1 && typeof pathData[keys[0]] === "object" && pathData[keys[0]] !== null) {
            pathData = pathData[keys[0]]
            setFieldKey(keys[0])
        }
        const isJson = isFieldsJson(fullData?.fields)

        if (pathData && isJson) {
            const temp = pathData;
            const type = isJson ? "json" : "";
            const name = fullData.sys.id || "";
            setTealiumExperience(JSON.stringify(temp))
            setOriginalTealiumExperience(JSON.stringify(temp))
            setType(type)
            setTealiumExperienceName(name)
            setFullEntryData(fullData)
            return true;
        } else {
            if (pathData && !isJson) {
                toast.error("Selected fragment is not of type JSON");
                navigate("experience/tealium-experiences");
                return;
            } else {
                return null;
            }
        }
        return pathData;
    };


    const handleUpdateTealiumExperience = async (showLoader = true) => {
        try {
            if (showLoader) setLoading(true);
            const experience_json = selectedTemplate.type === enums?.templateTypes.json_a_to_html.id 
                ? updateHtmlTemplate(selectedTemplate.html, fieldValues)
                : JSON.stringify(transformOfferJSON())
            const payload = {
                id: id,
                token: userData?.token,
                organizationId: userData?.selectedOrganization?.org_token,
                data: {
                    tealium_id: userData?.defaultTealiumKey?._id,
                    code: experience_json,
                },
            }

            const result = await updateTealiumExperience(payload);
            toast.success(result?.message);
        } catch (error) {
            toast.error(error?.message || "Failed to update entry.");
        } finally {
            if (showLoader) setLoading(false);
        }
    };

    function getFlattenExperienceJSON(ob) {
        let result = {};
        function recurse(cur, prop) {
            if (Object(cur) !== cur) {
                result[prop] = cur;
            } else if (Array.isArray(cur)) {
                for (let i = 0; i < cur.length; i++) {
                    recurse(cur[i], prop ? prop + "[" + i + "]" : i);
                }
                if (cur.length === 0) result[prop] = [];
            } else {
                let isEmpty = true;
                for (let p in cur) {
                    isEmpty = false;
                    recurse(cur[p], prop ? prop + "." + p : p);
                }
                if (isEmpty && prop) result[prop] = {};
            }
        }
        recurse(ob, "");
        return result;
    }

    const createTealiumExperience = async (values) => {
        try {
            if (!tealiumExperienceName) {
                setError(contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.errors.name_required)
                return;
            }
            if (validationError) return;

            if (showPreview) {
                const isValidate = handleValidateJson();
                if (isValidate.length > 0) return;
            }

            let content;
            if (showPreview && selectedTemplate?._id) {
                content = selectedTemplate.type === enums?.templateTypes.json_a_to_html.id 
                    ? updateHtmlTemplate(selectedTemplate.html, fieldValues)
                    : transformOfferJSON();
            } else {
                let raw = typeof tealiumExperience === 'string' ? tealiumExperience : (tealiumExperience?.configuration?.code ?? JSON.stringify(tealiumExperience));
                content = isValidJsonString(raw) ? JSON.parse(raw) : raw;
            }

            const payload = {
                tenant: userData.defaultAdobeIO?.tenant_name,
                data: {
                    name: values.offer_name,
                    workspace: userData.defaultWorkspace?.workspace_id,
                    content: content,
                    type: selectedTemplate?.type === enums.templateTypes.json_a_to_html.id ? 'content' : 'json',
                }
            }
            const result = await createTenantActivityOffers(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                payload
            )
            return result;
        } catch (error) {
            console.log('error', error)
            throw error;
        }
    };

    const createExperienceHandler = async (values, offerId=id) => {
        try {
            if (!selectedTemplate) {
                setError(contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.errors.template_required);
                return;
            }

            if (showPreview) {
                const isValidate = handleValidateJson();
                if (isValidate.length > 0) {
                    setError(isValidate.join(", \n"))
                    return
                }
            }

            if (validationError) {
                setError(validationError);
                return
            }

            const experience_json = selectedTemplate.type === enums?.templateTypes.json_a_to_html.id 
                    ? updateHtmlTemplate(selectedTemplate.html, fieldValues)
                : JSON.stringify(transformOfferJSON())

            const payload = {
                name: values.experience_name,
                templates: selectedTemplate?._id ? [selectedTemplate._id] : [],
                html: '',
                css: '',
                config_json: '',
                experience_json: experience_json,
                source: enums.experience_sources.offer_page.id,
                ...(offerId && { do_publish: true })   // flag will publish the created version
            }
            const result = await createExperience(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                payload
            );

            if (result?.data?.length > 0 && offerId) {
                const mappingPayload = {
                    experience_id: result.data[0]?._id,
                    id: offerId,
                    integration_id: userData?.defaultTealiumKey?._id,
                    destination: "tealium"
                };
                await editExperienceOfferMapping(
                    userData?.token,
                    userData?.selectedOrganization?.org_token,
                    mappingPayload
                );
            }

            setExperience(result?.data)
            return result;
        } catch (error) {
            throw error;
        }
    }

    const handleUpdateAssociatedExperiences = async () => {
        if (associatedExperiences.length <= 0) {
            toast.error("No associated experiences to update.");
            return;
        }
        try {
            setLoading(true);
            const experience_json = selectedTemplate.type === enums?.templateTypes.json_a_to_html.id 
                    ? updateHtmlTemplate(selectedTemplate.html, fieldValues)
                : JSON.stringify(transformOfferJSON())

            await Promise.all(
                associatedExperiences.map(exp => {
                    const payload = {
                        name: exp.name,
                        templates: selectedTemplate?._id ? [selectedTemplate._id] : [],
                        html: '',
                        css: '',
                        config_json: '',
                        experience_json: experience_json,
                        version: exp.published_version  // for updating published version which can update all associated activations
                    };
                    return editExperience(
                        userData?.token,
                        userData?.selectedOrganization?.org_token,
                        exp.experience_id,
                        userData?.defaultAdobeIO?._id,
                        userData?.defaultContentful?._id,
                        userData?.defaultTealiumKey?._id,
                        payload
                    );
                })
            );
            toast.success("Associated experiences updated successfully");
        } catch (err) {
            toast.error(err?.message || "Failed to update associated experiences");
        } finally {
            setLoading(false);
        }
    };

    const handleDelinkExperience = async () => {
        if (associatedExperiences.length <= 0) {
            toast.error("No associated experiences to delink.");
            return;
        }
        try {
            setLoading(true);
            await Promise.all(
                associatedExperiences.map(exp =>
                    delinkOfferFromExperience(
                        userData?.token,
                        userData?.selectedOrganization?.org_token,
                        {
                            experience_id: exp.experience_id,
                            id: id,
                            integration_id: userData?.defaultTealiumKey?._id,
                            destination: "tealium",
                        }
                    )
                )
            );
            setAssociatedExperiences([]);
            await handleUpdateTealiumExperience(false)
            toast.success("Offer delinked from associated experiences");
        } catch (err) {
            toast.error(err?.message || "Failed to delink offer");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (currentStep == 'choose-template' && selectedTemplate._id) {
            if (!isValidJsonString(selectedTemplate?.json_schema)) {
                setError(contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.errors.valid_json)
                return;
            }
            if (isValidJsonString(tealiumExperience.configuration.code)) {
                const tealiumJson = JSON.parse(tealiumExperience.configuration.code)
                const compare = compareJson(JSON.parse(selectedTemplate?.json_schema), tealiumJson);
                if (compare.length == 0) {
                    setFieldValues(getFlattenExperienceJSON(tealiumJson));
                } else {
                    setError(`${compare.join(", ")}${contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.errors.templates_not_same}`)
                    return;
                }
            } else {
                const parsedJsonSchema = JSON.parse(selectedTemplate?.json_schema)
                const updatedExperienceJson = getObjFromHtml(tealiumExperience.configuration.code, parsedJsonSchema?.config_obj_key)
                const compare = compareJson(updatedExperienceJson, parsedJsonSchema);

                if ((compare.length == 0) || (compare.length == 1 && compare.includes("experience_id"))) {
                    setFieldValues(getFlattenExperienceJSON(updatedExperienceJson));
                } else {
                    setError(`${compare.join(", ")}${contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.errors.templates_not_same}`)
                    return;
                }
            }
        }
        if (selectedTemplate?._id && currentStep == 'choose-template') {
            setCurrentStep('configure-offer');
        }
    }, [selectedTemplate])

    const handleNext = () => {
        if (currentStep == 'choose-template' && !selectedTemplate._id) {
            setError(contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.errors.template_required);
            return;
        }
        if (currentStep == 'choose-template' && selectedTemplate._id) {
            if (!isValidJsonString(selectedTemplate?.json_schema)) {
                setError(contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.errors.valid_json)
                return;
            }
            const AEMJson = JSON.parse(tealiumExperience)
            const compare = compareJson(JSON.parse(selectedTemplate?.json_schema), AEMJson);
            if (compare.length == 0) {
                setFieldValues(getFlattenExperienceJSON(AEMJson));
            } else {
                setError(`${compare.join(", ")}${contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.errors.templates_not_same}`)
                return;
            }
        }
        if (currentStep == 'choose-template') {
            setCurrentStep('configure-offer');
        } else if (currentStep == 'configure-offer') {
            setCurrentStep('update-offer');
        }
    }

    function transformOfferJSON() {
        const transformedJSON = {};
        for (const key in fieldValues) {
            const keys = key.split('.');
            keys.reduce((acc, part, index) => {
                if (part.includes('[')) {
                    const [arrayKey, arrayIndex] = part.split(/\[|\]/).filter(Boolean);
                    if (!acc[arrayKey]) {
                        acc[arrayKey] = [];
                    }
                    if (!acc[arrayKey][arrayIndex]) {
                        acc[arrayKey][arrayIndex] = {};
                    }
                    if (index === keys.length - 1) {
                        acc[arrayKey][arrayIndex] = fieldValues[key];
                    }
                    return acc[arrayKey][arrayIndex];
                } else {
                    if (index === keys.length - 1) {
                        acc[part] = fieldValues[key];
                    } else {
                        if (!acc[part]) {
                            acc[part] = {};
                        }
                        return acc[part];
                    }
                }
            }, transformedJSON);
        }
        return transformedJSON;
    }

    const handleValidateJson = (obj) => {
        setValidationError("");
        // const json = obj || selectedTemplate.config_json;

        const config_elements = [];
        JSON.parse(selectedTemplate.config_json).forEach((el) => {
            el.error = "";
            config_elements.push({ ...el, id: el.id.replace("[", ".").replace("]", "") });
        });
        const newConfigElements = [...config_elements];
        const errs = [];

        for (let item of newConfigElements) {
            const val = fieldValues[item.id];
            const rules = item.rules;
            if (rules.is_mandatory) {
                if (val?.length == 0) {
                    const message = "Field is required";
                    item.error = message;
                    errs.push(`${item.id}: ${message}`)
                } else {
                    item.error = "";
                }
            }
            if (rules.min_length && rules.max_length && item.error.length == 0) {
                if (val.length > rules.max_length || val.length < rules.min_length) {
                    const message = `Min length: ${rules.min_length} and Max length: ${rules.max_length}`;
                    item.error = message;
                    errs.push(`${item.id}: ${message}`);
                } else {
                    item.error = "";
                }
            }
        }

        if (errs.length > 0) {
            setValidationError(errs.join(", \n"));
        }
        return errs;
    }

    const handleSubmit = async (action, values) => {
        try {
            setError('');
            setLoading(true);
            let successMsg, result;
            switch (action) {
                case contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.activate_at.id:
                    if (!values?.offer_name) {
                        setError("Offer name is required.")
                        return;
                    }
                    result = await createTealiumExperience(values);
                    successMsg = `${contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.activate_at.success}. Offer Id - ${result.data.id}, Workspace Name: ${userData.defaultWorkspace?.workspace_name}`;
                    break;

                case contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.save_experience.id:
                    if (!values?.experience_name) {
                        setError("Experience name is required.")
                        return;
                    }
                    result = await createExperienceHandler(values);
                    successMsg = contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.save_experience.success
                    break;

                case contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.both.id:
                    if (!values?.experience_name || !values?.offer_name) {
                        setError("Fields are required.")
                        return;
                    }
                    const offerResult = await createTealiumExperience(values);
                    result = await createExperienceHandler(values, offerResult?.data?.id);
                    successMsg = `${contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.both.success}. Offer Id - ${offerResult.data.id}, Workspace Name: ${userData.defaultWorkspace?.workspace_name}`;
                    break;

                default:
                    break;
            }

            if (result) {
                setSuccessMessage(successMsg);
                setTimeout(() => {
                    navigate("/experience/tealium-experiences")
                }, 1500);
            }
        } catch (error) {
            console.log('error', error)
            setError(error?.message || contentfulData?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    };

    const handleAction = (action) => {
        switch (action) {
            case contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.update_tealium.id:
                setShowUpdateOfferModal(true)
                // handleUpdateTealiumExperience()
                break;
            case contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.activate_at.id:
                setCurrentStep("activate")
                break;
            case contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.save_experience.id:
                setShowCreateOfferModal({
                    action,
                    fields: [contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.save_experience.id]
                })
                break;
            case contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.both.id:
                setShowCreateOfferModal({
                    action,
                    fields: [contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.activate_at.id, contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.save_experience.id]
                })
                break;
            default:
                console.log(`Unknown action: ${action}`);
        }
    }

    const handleCloseModal = () => {
        setShowCreateOfferModal(null)
    }

    useEffect(() => {
        if (currentStep == "configure-offer") {
            enablePreviewMode();
        } else {
            disablePreviewMode();
        }
    }, [currentStep])

    return (
        <div className='w-full mx-auto overflow-auto sticky z-[15]'>
            {loading && <Loader />}
            {error && <ErrorAlert timeout="3000">{error}</ErrorAlert>}
            {successMessage && <SuccessAlert disappearTime={4000}>{successMessage}</SuccessAlert>}
            <div className='py-2 px-4 md:px-8 bg-gray-1 mb-2 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4'>
                <div className='w-full md:max-w-[260px]'>
                    <Input
                        size="sm"
                        disabled
                        id="offer_name"
                        name="offer_name"
                        value={tealiumExperienceName}
                        placeholder={contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.tealium_experience_name}
                    />
                </div>
                {!showPreview && !isStepperHidden && <div className='flex justify-end gap-3'>
                    {checkPermission(permission.AEM_CONTENT_FRAGMENT_OP_CREATE_PREVIEW) && <SecondaryButton size={'base'} handleClick={() => setShowPreview(true)}>{contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.show_preview_cta}</SecondaryButton>}
                    {
                        checkPermission(permission.CONTENTFUL_ENTRY_OP_OFFER) && <PrimaryButton size={'base'}
                            handleClick={() => {
                                setCurrentStep("activate")
                                setIsStepperHidden(true)
                            }}
                        >
                            {contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.activate_at.name}
                        </PrimaryButton>
                    }
                </div>}
                {showPreview && <ol className="flex items-center justify-center flex-1 text-xs font-medium text-center text-gray-500 gap-2 md:gap-0">
                    <li className="flex md:w-full items-center sm:after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10">
                        <button className="text-xs flex items-center flex-col after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200" onClick={() => {
                            setTealiumExperience(originalTealiumExperience)
                            setCurrentStep("choose-template");
                            setSelectedTemplate({})
                            setFieldValues()
                        }}
                        >
                            <img alt='' src={tickImg} className="w-4 h-4" />
                            <span className="hidden lg:block">{contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.choose_template}</span>
                        </button>
                    </li>
                    <li className="text-xs flex items-center">
                        <button className="flex items-center flex-col" onClick={() => {
                            if (currentStep == "update-offer") {
                                setCurrentStep("configure-offer")
                            }
                        }}>
                            {(currentStep == 'configure-offer' || currentStep == 'update-offer' || currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">2</span>}
                            <span className="hidden lg:block">{contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.configure}</span>
                        </button>
                    </li>
                    <li className="text-xs flex md:w-full items-center before:content-[''] before:w-full before:h-1 before:border-b before:border-gray-200 before:border-1 before:hidden sm:before:inline-block before:mx-6 xl:before:mx-10">
                        <button className="flex items-center flex-col before:content-['/'] sm:before:hidden before:mx-2 before:text-gray-200">
                            {(currentStep == 'update-offer' || currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">3</span>}
                            <span className="hidden lg:block">{contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.update_contentful}</span>
                        </button>
                    </li>
                    <li className="text-xs flex md:w-full items-center before:content-[''] before:w-full before:h-1 before:border-b before:border-gray-200 before:border-1 before:hidden sm:before:inline-block before:mx-6 xl:before:mx-10">
                        <button className="flex items-center flex-col before:content-['/'] sm:before:hidden before:mx-2 before:text-gray-200">
                            {(currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">4</span>}
                            <span className="hidden lg:block">{contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.activate}</span>
                        </button>
                    </li>
                </ol>}
                {
                    currentStep == 'activate' && !showPreview && <SecondaryButton size={'base'} handleClick={() => navigate("/experience/tealium-experiences")} wrapperClassName="w-full md:w-auto">{contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.cancel_btn}</SecondaryButton>
                }
                {showPreview && <div className='flex justify-end gap-2 md:gap-3 w-full md:w-auto'>
                    {<SecondaryButton size={'base'} handleClick={() => navigate("/experience/tealium-experiences")}>{contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.cancel_btn}</SecondaryButton>}
                    {currentStep == "update-offer" ? (
                        checkPermission(permission.EDIT_OFFER) && <DropdownButton handleSubmit={handleAction} />
                    ) : (currentStep !== "choose-template" && currentStep !== 'activate') ? (
                        <PrimaryButton size={'base'} handleClick={handleNext}>{contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.next_btn}</PrimaryButton>
                    ) : null}
                </div>}
            </div>
            <div className='h-full max-h-[calc(100vh-80px)] px-4 md:px-8'>
                {currentStep == 'choose-template' && tealiumExperience && (
                    <ChooseTemplate
                        title={contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.title}
                        offerJson={{ 
                            content: isValidJsonString(tealiumExperience.configuration.code)
                                ? JSON.parse(tealiumExperience.configuration.code)
                                : tealiumExperience.configuration.code,
                        }}
                        selectedTemplate={selectedTemplate}
                        setSelectedTemplate={setSelectedTemplate}
                        hideTemplates={!showPreview}
                        type={type}
                    />
                )}
                {currentStep == 'configure-offer' && (
                    <ConfigureExperience
                        setCurrentStep={setCurrentStep}
                        selectedTemplate={selectedTemplate}
                        setSelectedTemplate={setSelectedTemplate}
                        fieldValues={fieldValues}
                        setFieldValues={setFieldValues}
                        setTransformedExperienceJSON={setTransformedExperienceJSON}
                        actionText={contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.edit_contentful_entry}
                    />
                )}
                {currentStep == 'update-offer' &&
                    <OfferCode
                        title={contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.title}
                        experienceJSON={transformedExperienceJSON}
                        selectedTemplate={selectedTemplate}
                        offerJson={tealiumExperience}
                        getFlattenExperienceJSON={getFlattenExperienceJSON}
                        setFieldValues={setFieldValues}
                        validationError={validationError}
                        setValidationError={setValidationError}
                        handleValidateJson={handleValidateJson}
                    />
                }
                {currentStep == 'activate' &&
                    <>
                        <h1 className="text-xl px-6 py-4 font-extrabold leading-tight tracking-tight text-gray-900 md:text-2xl">{contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.dropdown_btn.activate_at.name}</h1>
                        <Activation
                            experience={experience}
                            setExperience={setExperience}
                            validationError={validationError}
                            setValidationError={setValidationError}
                            handleValidateJson={handleValidateJson}
                            offerPermission={{
                                create: permission.CREATE_EXP_OP_CREATE_OFFER
                            }}
                            contentfulPermission={{
                                create: permission?.CREATE_EXP_OP_CREATE_CONTENTFUL
                            }}
                            selectedTemplate={isStepperHidden ? null : selectedTemplate}
                            code={showPreview ? transformOfferJSON() : tealiumExperience}
                            offerActionConfig={{
                                handler: createTealiumExperience,
                                source: "tealium"
                            }}
                            tealiumPermission={{
                                create: permission.CREATE_EXP_OP_CREATE_TEALIUM,
                                edit: permission.CREATE_EXP_OP_UPDATE_TEALIUM
                            }}
                        />
                    </>
                }
                {showCreateOfferModal && (
                    <CreateOfferAndExperienceModal
                        error={error}
                        title={contentfulData.TEALIUM_EXPERIENCE_DETAILS_CONTENT.title}
                        loading={loading}
                        handleSubmit={handleSubmit}
                        handleCloseModal={handleCloseModal}
                        offerSuccessMessage={successMessage}
                        showCreateOfferModal={showCreateOfferModal}
                    />
                )}

                {
                    jsonPathModal?.isOpen && jsonPathModal?.tealiumExperience && (
                        <EditJsonPathModal
                            onClose={jsonPathModalClose}
                            type={jsonPathModal?.type}
                            data={jsonPathModal?.tealiumExperience}
                            onSuccess={updateTealiumContentFromPath}
                            source="tealium"
                        />
                    )
                }

                {showUpdateOfferModal && (
                    <UpdateTealiumAssociationModal
                        code={showPreview ? transformOfferJSON() : tealiumExperience}
                        isOpen={showUpdateOfferModal}
                        onClose={() => setShowUpdateOfferModal(false)}
                        associatedExperiences={associatedExperiences}
                        onUpdateAssociatedExperience={handleUpdateAssociatedExperiences}
                        onDelinkExperience={handleDelinkExperience}
                        loading={loading}
                    />
                )}
            </div>
        </div>
    )
}

export default TealiumExperienceDetails;